<script lang="ts">
  // Marketing-Surface Preview panel per INTERFACE-SPECIFICATION.md §6.2.
  //
  // Displays the public-band aggregation for the active product.
  // Commit 5 wires the cube editor to supply `stagedCube` so the
  // panel re-aggregates live as cube cells are staged.

  import type { ProductCatalogueFile } from '../lib/types';
  import { projectMarketingBand, PUBLIC_BANDS } from '../lib/surface-projector';
  import { formatRate, formatRateDelta, deltaCssClass } from '../lib/formatters';

  interface Props {
    catalogue: ProductCatalogueFile;
    stagedCube: ProductCatalogueFile | null;
  }

  const { catalogue, stagedCube }: Props = $props();

  const rows = $derived(
    PUBLIC_BANDS.map((band) => {
      const current = projectMarketingBand(catalogue, band);
      const staged = stagedCube ? projectMarketingBand(stagedCube, band) : null;
      const delta = staged && current.min_rate !== null && staged.min_rate !== null
        ? staged.min_rate - current.min_rate
        : null;
      return { band, current, staged, delta };
    })
  );

  const bandLabel: Record<string, string> = { excellent: 'Excellent', good: 'Good', fair: 'Fair' };
</script>

<section class="card preview-card">
  <h3 class="section-heading">Marketing-Surface Preview</h3>
  <p class="muted">Live aggregation. The customer-visible public-band rates derive deterministically from the cube.</p>
  <table class="cube-table">
    <thead>
      <tr>
        <th>Public band</th>
        <th class="num">% Current min</th>
        {#if stagedCube}
          <th class="num">% Staged min</th>
          <th class="num">Δ %</th>
        {/if}
      </tr>
    </thead>
    <tbody>
      {#each rows as row (row.band)}
        <tr>
          <td>{bandLabel[row.band]}</td>
          <td class="num">{formatRate(row.current.min_rate)}</td>
          {#if stagedCube}
            <td class="num">{formatRate(row.staged?.min_rate)}</td>
            <td class="num {deltaCssClass(row.delta)}">{formatRateDelta(row.delta)}</td>
          {/if}
        </tr>
      {/each}
    </tbody>
  </table>
</section>

<style>
  .preview-card {
    background: var(--surface-card);
    padding: var(--space-4);
  }
</style>
